import { CustomNodeType, generateNodeConfig } from './nodes';
import { ActionBtns } from './nodes/common';
import { TreeGraphData } from '@antv/g6';
import {
  fullMoneyOptions,
  operationTypeOptions,
  startPkgOptions,
  activityTypeOptions,
  fullGwpRightsOperationOptions,
  fullSubRightsOperationOptions,
  fullGwpTimesOptions,
  fullSubTimesOptions
} from './options';
import { generateRandomNumber } from './utils';
import { cloneDeep } from 'lodash-es';

export type TNodeData = TreeGraphData & Partial<ReturnType<typeof createJsonData>> & Record<string, any>;

export const createConditionRowJsonData = (isPre = false) => {
  const conditionId = generateRandomNumber();
  return {
    id: conditionId,
    _uniqueId: conditionId,
    specialCharacters: conditionId,
    optionType: operationTypeOptions[0].value, // 操作类型：满金额，满数量，满款数等
    optionValue: 1, // 数值
    orderUnit: startPkgOptions[0].value, // 起订单位：xx起订
    fullNumber: 1, // 需满数值
    moneyUnit: fullMoneyOptions[0].value, // 需满单位-金额，操作类型为满金额时填
    itemUnit: '', // 需满单位-商品，操作类型为满款数时填
    packageUnit: '', // 需满单位-包装，操作类型为满数量时填
    promotionItemStr: '', // 商品清单名称
    checked: false, // 是否勾选(仅前端使用)
    checkedDisabled: false // 勾选是否禁用(仅前端使用)
  };
};

export const createJsonData = (id, parentId, level) => {
  return {
    id,
    parentId,
    name: '',
    hasPrecondition: false, // 是否开启前置条件
    level, // 节点层级
    // 前置条件
    conditions: [] as any[],
    conditionDesc: '', // 活动提示文案
    logicalExpression: '', // 前置条件表达式
    // 促销政策
    policyInfo: {
      name: '',
      // 主题名称
      policyThemes: '',
      // 政策标签
      policyLabel: '',
      // 下单金额限制
      orderNumberLimit: 0,
      // 记账时间规则
      accountTimeRule: '',
      // 账期天数
      paymentDays: '',
      // 记账时间
      accountDate: '',
      // 促销类型
      promotionType: ''
    },
    promotionItemStr: '', // 促销政策商品清单
    priority: 1 as number | string, // 活动类型优先级
    prevPriority: '' as number | string, // 上一次活动类型优先级(修改之前的值，仅前端使用)
    // promotionSuperpositionPriority: '', // 叠加活动的优先级（每个活动只有一个角色）
    optionType: '', // 操作类型
    effectConditions: [] as any[], // 满减/满赠门槛规则
    effectConditionDesc: '', // 活动提示文案
    effectLogicalExpression: '', // 满减/满赠门槛规则表达式
    // 权益信息
    equityInformationRule: {
      name: '',
      directDescentOption: '', // 直降门槛规则
      reductionOption: fullSubRightsOperationOptions[0].value, //活动类型=满减时的优惠操作
      giftOption: fullGwpRightsOperationOptions[0].value, //活动类型=满赠时的优惠操作
      reductionDiscount: fullSubTimesOptions[0].value, //操作类型为满减时的优惠类型
      giftDiscount: fullGwpTimesOptions[0].value, //操作为满赠时的优惠类型
      discountValue: 0, //优惠数值
      giftItemStr: '', // 满赠清单
      equityInfoItemStr: '', // 权益信息清单
      realPriceNotBenefits: true, // 实价产品不让利
      buyItNotBenefits: true, // 一口价产品不让利
      isAdvanced: false, // 是否高级配置
      advanceItemStr: '' // 高级配置清单
    },
    children: [] as any[],
    // 仅前端使用, 前置条件组合
    conditionsCombine: {
      type: 'and',
      groups: [] as any[]
    },
    // 仅前端使用, 门槛规则组合
    effectConditionsCombine: {
      type: 'and',
      groups: [] as any[]
    }
  };
};

export const rootNodeData = ({ jData = undefined as any, rowData }): TreeGraphData => {
  const id = jData ? jData.id : generateRandomNumber();
  const jsonData = jData ? cloneDeep(jData) : createJsonData(id, '', 1);
  jsonData.children = [];

  const rd = {
    ...jsonData,
    type: CustomNodeType.PLAN
  };

  if (rowData[id]) {
    Object.assign(rowData[id], rd);
  } else {
    rowData[id] = rd;
  }

  return {
    id,
    dataId: id, // 业务数据id
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: {
      switchAction: { open: false, show: false },
      addAction: true,
      deleteAction: true
    } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.PLAN),
    children: [] as TreeGraphData[]
  };
};

export const createConditionData = ({ isPre = false, jsonData, isEdit = false, rowData }) => {
  const conditionRow = createConditionRowJsonData(true);
  const effectConditionRow = createConditionRowJsonData();
  const id = generateRandomNumber();
  jsonData = cloneDeep(jsonData);
  jsonData.children = [];
  const rd = {
    ...jsonData,
    type: CustomNodeType.CONDITIONS
  };
  let curRowData;
  if (rowData[jsonData.id]) {
    Object.assign(rowData[jsonData.id], rd);
    curRowData = rowData[jsonData.id];
  } else {
    rowData[id] = rd;
    curRowData = rowData[id];
  }

  const c = {
    id,
    dataId: jsonData.id, // 业务数据id
    title: isPre ? '前置条件' : '门槛规则',
    isPre,
    style: {
      titleBgColor: isPre ? '#d70a25' : 'var(--oio-button-primary-background)'
    },
    actionBar: { settingAction: jsonData.optionType === 'DIRECT_DESCENT' && !isPre } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.CONDITIONS),
    children: [] as TreeGraphData[]
  };

  if (!isEdit) {
    if (isPre) {
      curRowData.conditions = [conditionRow];
      curRowData.conditionsCombine.groups = [
        {
          _uniqueIds: [conditionRow._uniqueId],
          type: 'and'
        }
      ];
    } else {
      curRowData.effectConditions = [effectConditionRow];
      curRowData.effectConditionsCombine.groups = [
        {
          _uniqueIds: [effectConditionRow._uniqueId],
          type: 'and'
        }
      ];
    }
  }

  return c;
};

export const createSimpleConditionData = ({ jsonData, rowData }) => {
  const id = generateRandomNumber();
  jsonData = cloneDeep(jsonData);
  jsonData.children = [];
  const rd = {
    ...jsonData,
    type: CustomNodeType.SIMPLE_CONDITION
  };

  if (rowData[jsonData.id]) {
    Object.assign(rowData[jsonData.id], rd);
  } else {
    rowData[id] = rd;
  }

  return {
    id,
    dataId: jsonData.id, // 业务数据id
    title: '门槛规则',
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: { settingAction: false } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.SIMPLE_CONDITION),
    children: [] as TreeGraphData[]
  };
};

export const createPolicyData = ({ parentId, jData = undefined as any, rowData }) => {
  const uid = jData ? jData.id : generateRandomNumber();
  const jsonData = jData ? cloneDeep(jData) : createJsonData(uid, parentId, 2);
  jsonData.children = [];

  const rd = {
    ...jsonData,
    type: CustomNodeType.POLICY
  };

  if (rowData[uid]) {
    Object.assign(rowData[uid], rd);
  } else {
    rowData[uid] = rd;
  }

  return {
    id: uid,
    dataId: uid, // 业务数据id
    style: {
      titleBgColor: '#aaa'
    },
    actionBar: {
      switchAction: { open: false, show: true },
      linkAction: true,
      addAction: true,
      settingAction: true,
      deleteAction: true
    } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.POLICY),
    children: [] as TreeGraphData[]
  };
};

export const createActivityTypeData = ({ parentId, jData = undefined as any, rowData }) => {
  const uid = jData ? jData.id : generateRandomNumber();
  const jsonData = jData ? cloneDeep(jData) : createJsonData(uid, parentId, 3);
  if (!jData) {
    jsonData.optionType = activityTypeOptions[0].value;
  }
  jsonData.children = [];
  const rd = {
    ...jsonData,
    type: CustomNodeType.ACTIVITY_TYPE
  };
  if (rowData[uid]) {
    Object.assign(rowData[uid], rd);
  } else {
    rowData[uid] = rd;
  }

  return {
    id: uid,
    dataId: uid, // 业务数据id
    title: '活动类型',
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: { switchAction: { open: false, show: true }, deleteAction: true } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.ACTIVITY_TYPE),
    children: [] as TreeGraphData[]
  };
};

export const createRightsInfoData = ({ jsonData, rowData }) => {
  const id = generateRandomNumber();
  jsonData = cloneDeep(jsonData);
  jsonData.children = [];

  const rd = {
    ...jsonData,
    type: CustomNodeType.RIGHTS_INFO
  };

  if (rowData[jsonData.id]) {
    Object.assign(rowData[jsonData.id], rd);
  } else {
    rowData[id] = rd;
  }

  return {
    id,
    dataId: jsonData.id, // 业务数据id
    title: '权益信息',
    activityType: activityTypeOptions[0].value, // 活动类型
    col1DisabledOptions: [] as string[], // 第一列禁用选项
    policyId: '', // 所属促销政策节点id
    style: {
      titleBgColor: '#f39a35'
    },
    actionBar: {
      linkAction: true,
      settingAction: true
    } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.RIGHTS_INFO),
    children: [] as TreeGraphData[]
  };
};
